// Components
import { IconHistory, IconListSearch } from '@u21/tabler-icons';
import {
  U21Button,
  U21InfoItem,
  U21Spacer,
  U21Subsection,
  U21TitleCountLabel,
  U21Typography,
} from 'app/shared/u21-ui/components';
import pluralize from 'pluralize';

import { useState, useMemo } from 'react';
import { formatDatetime } from 'app/shared/utils/date';
import { AlertHitsResponse } from 'app/modules/alerts/types/responses';
import {
  isAdvancedModel,
  isIRSchema,
} from 'app/modules/detectionModels/helpers';
import { SidebarDataRow } from 'app/modules/sidebar/components/utils/SidebarDataRow';
import { ConditionBreakdown } from 'app/modules/detectionModels/components/ConditionBreakdown/ConditionBreakdown';
import { useFetchRule } from 'app/modules/rules/queries/useFetchRule';
import { ShortRuleResponse } from 'app/modules/rules/types';
import { RuleStatus } from 'app/modules/rules/models';
import { RuleConditionTooltips } from 'app/modules/detectionModels/constants';
import styled from 'styled-components';

interface Props {
  count: number | null;
  alertUpdates: AlertHitsResponse;
  rule: ShortRuleResponse;
  alertId: number;
}

export const SidebarAlertUpdates = ({
  count,
  alertUpdates,
  rule,
  alertId,
}: Props) => {
  const [selectedHitIndex, setSelectedHitIndex] = useState<number>(-1);
  const [open, setOpen] = useState<boolean>(false);

  const { data: fullRule } = useFetchRule(rule.id, rule.status as RuleStatus);

  const ruleCondition = useMemo(
    () =>
      fullRule?.content.specification &&
      isAdvancedModel(fullRule) &&
      isIRSchema(fullRule.content.specification)
        ? fullRule.content.specification.rule_condition
        : null,
    [fullRule],
  );

  const getLabel = (idx: number, createdAt: string) => {
    const prefix = idx === alertUpdates.count - 1 ? 'Flagged at' : 'Updated at';
    return `${prefix} ${formatDatetime(createdAt)}`;
  };

  const content = alertUpdates.hits.map((hit, idx) => (
    <U21Spacer key={hit.created_at}>
      <U21InfoItem
        label={getLabel(idx, hit.created_at)}
        labelProps={{
          style: {
            marginTop: '10px',
          },
        }}
        width={275}
        enableCopy={false}
      >
        <StyledU21Button
          disabled={
            !(fullRule && isAdvancedModel(fullRule)) ||
            Object.keys(alertUpdates.hits[idx].variable_snapshots).length === 0
          }
          aria-label={RuleConditionTooltips.VIEW_RULE_CONDITIONS}
          tooltip={
            Object.keys(alertUpdates.hits[idx].variable_snapshots).length !== 0
              ? RuleConditionTooltips.VIEW_RULE_CONDITIONS
              : RuleConditionTooltips.RULE_CONDITIONS_NOT_AVAILABLE
          }
          onClick={(e) => {
            e.stopPropagation();
            setSelectedHitIndex(idx);
            setOpen(true);
          }}
          size="small"
        >
          <IconListSearch />
        </StyledU21Button>
      </U21InfoItem>
      {(hit.new_txn_events_count > 0 || hit.new_action_events_count > 0) && (
        <SidebarDataRow label="Events" showEmptyFields enableCopy={false}>
          {`${hit.new_txn_events_count + hit.new_action_events_count} new ${pluralize('event', hit.new_txn_events_count + hit.new_action_events_count)}`}
        </SidebarDataRow>
      )}
      {hit.new_entities_count > 0 && idx === alertUpdates.hits.length - 1 && (
        <SidebarDataRow label="Entities" showEmptyFields enableCopy={false}>
          {`${hit.new_entities_count} new ${pluralize('entity', hit.new_entities_count)}`}
        </SidebarDataRow>
      )}
      {hit.new_instruments_count > 0 &&
        idx === alertUpdates.hits.length - 1 && (
          <SidebarDataRow
            label="Instruments"
            showEmptyFields
            enableCopy={false}
          >
            {`${hit.new_instruments_count} new ${pluralize('instrument', hit.new_instruments_count)}`}
          </SidebarDataRow>
        )}
    </U21Spacer>
  ));

  return (
    <U21Subsection
      aria-label="Alert Updates"
      collapsible
      collapsed
      shaded
      title={
        <U21TitleCountLabel count={count} label="">
          Alert Updates
        </U21TitleCountLabel>
      }
      titleIcon={<IconHistory />}
    >
      {alertUpdates.hits.length ? (
        content
      ) : (
        <U21Typography variant="caption" color="text.secondary">
          No alert updates
        </U21Typography>
      )}
      {alertUpdates.hits.length > 0 && (
        <ConditionBreakdown
          open={open}
          close={() => setOpen(false)}
          ruleCondition={ruleCondition}
          ruleTitle={rule?.title}
          alertId={alertId}
          alertHits={alertUpdates.hits}
          alertHitIndex={selectedHitIndex}
          totalAlertHitIndex={alertUpdates.hits.length}
          onHeaderAction={(ind: number) => {
            setSelectedHitIndex(ind);
          }}
        />
      )}
    </U21Subsection>
  );
};

const StyledU21Button = styled(U21Button)`
  margin-top: 5px;
`;
